// eslint-disable-next-line no-unused-vars
import { cloneElement, useContext, useMemo, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { position, layout } from 'styled-system';
import { motion, AnimatePresence } from 'framer-motion';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import UIContext from '~/ui/ui-context';
import breakpoint, { getZindexByType } from '~/utils/style-utils';
import {
	modalTop,
	calcModalWidth,
	calcModalHeight,
	modalLeft,
	calcTransform,
	modalZIndex,
	wrapperMarginTop,
	wrapperHeight
} from '~/utils/layout-utils.js';

const variantsFromBottom = {
	enter: {
		y: 0,
		transitionEnd: { y: 0 },
		transition: {
			type: 'spring',
			damping: 16,
			duration: 0.2
		}
	},
	exit: {
		y: '100%',
		transition: {
			type: 'spring',
			damping: 16,
			duration: 0.2
		}
	}
};

const ModalStyles = styled(motion.div)`
	display: flex;
	justify-content: center;
	width: 100%;
	background-color: ${props => {
		if (props.noBg) {
			return '';
		}

		if (props.semiDarkBg) {
			return 'rgba(0,0,0, 0.5)';
		}

		return props.blurBg ? 'rgba(var(--base-11), 0.85)' : 'var(--sidebar-bg, rgba(var(--base-11), 1))';
	}};
	z-index: ${getZindexByType('modal')};
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	align-items: stretch;
	flex-flow: column nowrap;
	overflow: hidden;
	${position}
`;

const ModalView = styled.div`
	background-color: var(--sidebar-bg, rgba(var(--base-11), 1));
	width: 100%;
	height: 100%;
	opacity: 1;
	visibility: visible;
	left: 0;
	right: 0;
	top: 0;
	position: ${props => (props.position ? props.position : 'sticky')};
	z-index: ${getZindexByType('modal-view')};
`;

const SimpleModal = props => {
	return (
		<ModalStyles variants={variantsFromBottom} animate={props.open ? 'enter' : 'exit'} initial="exit" {...props}>
			{props.children}
		</ModalStyles>
	);
};

SimpleModal.defaultProps = {
	position: 'fixed'
};

const ModalWrapper = styled.div`
	width: 100%;
	height: 100%;
	bottom: 0;
	top: 0;
	pointer-events: ${props => (props.blurBg ? 'auto' : 'none')};
	z-index: ${props => modalZIndex(props)};
	background-color: ${props => (props.blurBg ? 'rgba(0,0,0,0.8)' : 'transparent')};
	position: ${props => (props.withinSidebar ? 'absolute' : props.position || 'fixed')};
	${breakpoint.lg`
		height: ${props => (props.customHeight ? props.customHeight : wrapperHeight(props))};
		margin-top: ${props => wrapperMarginTop(props)};
	`}

	${layout}
	animation: fadeInFromNone 0.3s linear;
	@keyframes fadeInFromNone {
		0% {
			display: none;
			opacity: 0;
		}
		1% {
			display: block;
			opacity: 0;
		}
		100% {
			display: block;
			opacity: 1;
		}
	}
`;

const ContentWrapper = styled.div`
	width: 100%;
	height: ${props => calcModalHeight(props)};
	overflow: hidden;
	bottom: 0;
	top: ${props => modalTop(props)};
	pointer-events: auto;
	${props =>
		props.modalHeight &&
		css`
			height: ${props.modalHeight}px;
		`}
	animation: modalAppear 500ms ease-in-out;
	@keyframes modalAppear {
		from {
			top: 100%;
		}
		to {
			top: ${props => modalTop(props)};
		}
	}
	background-color: ${props => (props.bgColor ? props.bgColor : 'transparent')};
	position: ${props => (props.withinSidebar ? 'absolute' : props.position || 'fixed')};
	${breakpoint.lg`
		width: ${props => calcModalWidth(props)};
		left: ${props => modalLeft(props)};
		transform: ${props => calcTransform(props)} !important;
		height: ${props => (props.withinSidebar ? 'auto' : 'fit-content')};
		border-radius: ${props => (props.brRadius ? props.brRadius : '0')}px;
		${props =>
			props.modalHeight &&
			css`
				height: ${props.modalHeight}px;
			`}
	`}
`;

const Content = ({ children, onToggleShow }) =>
	cloneElement(children, {
		...(onToggleShow ? { onToggleShow } : {})
	});

export const ModalBase = ({
	show,
	onToggleShow,
	children,
	small,
	blurBg,
	setTopToZero = false,
	welcomePopup = false,
	notificationPopup = false,
	bodyScrollDisabled,
	...rest
}) => {
	const modalRef = useRef(null);

	const { withinSidebar, hasStaticHeader, hasHeader, hasProductNav, showSidebar } = useContext(UIContext);

	useEffect(() => {
		if (show) {
			if (bodyScrollDisabled) {
				disableBodyScroll(modalRef.current);
			} else {
				enableBodyScroll(modalRef.current);
			}
		}
		return () => clearAllBodyScrollLocks();
	}, [modalRef, show, bodyScrollDisabled]);

	if (!show) return null;

	return (
		<ModalWrapper
			key="modal-x"
			ref={modalRef}
			blurBg={blurBg}
			notificationPopup={notificationPopup}
			hasStaticHeader={hasStaticHeader}
			hasHeader={hasHeader}
			hasProductNav={hasProductNav}
			withinSidebar={withinSidebar}
			{...rest}
		>
			<AnimatePresence>
				<ContentWrapper
					small={small}
					variants={variantsFromBottom}
					withinSidebar={withinSidebar}
					hasStaticHeader={hasStaticHeader}
					hasHeader={hasHeader}
					hasProductNav={hasProductNav}
					blurBg={blurBg}
					notificationPopup={notificationPopup}
					setTopToZero={setTopToZero}
					welcomePopup={welcomePopup}
					showSidebar={showSidebar}
					{...rest}
				>
					<Content onToggleShow={onToggleShow}>{children}</Content>
				</ContentWrapper>
			</AnimatePresence>
		</ModalWrapper>
	);
};

const mapStateToProps = ({ browser }) => ({
	small: browser.is.small
});

const Modal = connect(mapStateToProps)(ModalBase);

export { Modal, SimpleModal, ModalView, variantsFromBottom, ModalStyles, ModalWrapper };

export default Modal;
